import { LoadingOutlined } from "@ant-design/icons";
import {
  Button,
  Col,
  DatePicker,
  Form,
  Input,
  InputNumber,
  Modal,
  Row,
  Select,
  Spin,
} from "antd";
import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { GetActiveScalesApi } from "../../../api/GetActiveScalesApi";
import GetScaleUnitsByScaleApi from "../../../api/GetScaleUnitsByScaleApi";
import GetPlanTemplateByIdApi from "../../../api/plan-templates/GetPlanTemplateByIdApi";
import EditSplitPlanApi from "../../../api/plans/EditSplitPlanApi";
import moment from "moment";
import { GetUnitPositionsApi2 } from "../../../api/units/GetUnitPositionsApi2";
import {
  openNotificationError,
  openNotificationSuccess,
} from "../../../helpers/NotificationHelpers";

const { Option } = Select;

const { TextArea } = Input;

function EditSplitPlanModal(props) {
  const [submit, setSubmit] = useState(false);
  const [planLevels, setPlanLevels] = useState([]);
  const [planLevel, setPlanLevel] = useState([]);
  const [scales, setScales] = useState([]);
  const [scaleUnits, setScaleUnits] = useState([]);
  const [percentage, setPercentage] = useState(false);
  const [addScaleUnit, setAddScaleUnit] = useState(false);
  const [unitPositions, setUnitPositions] = useState([]);
  const perfCycleState = useSelector((state) => state.perfCycleState.value);
  const [form] = Form.useForm();
  const [valuesChanged, setValuesChanged] = useState(false);

  function handleCancel() {
    setSubmit(false);
    setPlanLevels([]);
    setPlanLevel([]);
    setScales([]);
    setScaleUnits([]);
    setUnitPositions([]);
    setPercentage(false);
    setAddScaleUnit(false);
    form.resetFields();
    props.handleCancel();
    setValuesChanged(false);
  }

  useEffect(() => {
    if (props.visible) {
      getActiveScales();
      getPlanLevels();
      getUnitUsers();
    }
  }, [props.visible]);

  useEffect(() => {
    if (planLevels) {
      selectPlanLevel(props.plan?.planLevelId);

      form.setFields([
        {
          name: "targetScale",
          value: props.plan?.scale?.scaleId,
        },
        {
          name: "target",
          value: props.plan?.target,
        },
        {
          name: "description",
          value: props.plan?.description,
        },
        {
          name: "targetDate",
          value: moment(props.plan?.targetDate),
        },
      ]);
    }
  }, [planLevels]);

  // Set attribute form values after planLevel is loaded
  useEffect(() => {
    if (planLevel && props.plan?.planAttributeValue) {
      // Set form values for attributes
      setTimeout(() => {
        props.plan.planAttributeValue.forEach((attribute) => {
          let fieldValue = "";
          
          if (attribute.value !== null && attribute.value !== "" && attribute.value !== "-") {
            if (attribute.valueType === "text") {
              fieldValue = attribute.text;
            } else if (attribute.valueType === "number") {
              fieldValue = attribute.number;
            } else if (attribute.valueType === "double") {
              fieldValue = attribute.double;
            } else if (attribute.valueType === "dropdown") {
              // Parse the dropdown value - use raw IDs from 'dropdown' field
              if (!attribute.dropdown || attribute.dropdown === "" || attribute.dropdown === "-") {
                fieldValue = []; // Empty array for no selections
              } else {
                // Parse comma-separated values or single value
                if (attribute.dropdown.includes(',')) {
                  fieldValue = attribute.dropdown.split(',').map(id => String(id).trim()).filter(id => id);
                } else {
                  fieldValue = [String(attribute.dropdown).trim()];
                }
              }
            } else if (attribute.valueType === "date") {
              fieldValue = moment(attribute.date);
            } else if (attribute.valueType === "boolean") {
              fieldValue = attribute.value;
            }
          } else {
            // Set appropriate empty values for different types
            if (attribute.valueType === "dropdown") {
              fieldValue = []; // Empty array for dropdowns
            } else {
              fieldValue = ""; // Empty string for others
            }
          }
          
          form.setFields([
            {
              name: attribute.id,
              value: fieldValue,
            },
          ]);
        });
      }, 100);
    }
  }, [planLevel, props.plan?.planAttributeValue]);

  async function getUnitUsers() {
    const apiResponse = await GetUnitPositionsApi2(props.plan?.unit?.unitId);
    if (apiResponse.message === "success") {
      setUnitPositions(apiResponse.data);

      form.setFields([
        {
          name: "assignTo",
          value: props.plan?.assignedTo?.positionId,
        },
      ]);
    }
  }

  async function onFinish(values) {
    setSubmit(true);

    let attributesValues = [];
    props.plan?.planAttributeValue?.forEach((attribute) => {
      attributesValues.push({
        attributeId: attribute.planAttributeId,
        value: values[attribute.id],
      });
    });

    const apiResponse = await EditSplitPlanApi(
      props.plan?.planId,
      values.title,
      values.description || "",
      values.target,
      values.targetDate || "",
      values.assignTo,
      attributesValues,
      values.targetScale || null,
      addScaleUnit ? values.unitScale || null : null,

      values.planLevel
    );
    if (apiResponse.statusCode) {
      openNotificationError("bottomRight", apiResponse.message);
    } else if (apiResponse.message === "success") {
      openNotificationSuccess("bottomRight", `Split plan edited successfully`);
      props.handleOk(apiResponse.data);
    } else {
      openNotificationError(
        "bottomRight",
        "Couldn't split plan. Try again later."
      );
    }
    handleCancel();
  }

  async function getPlanLevels() {
    let newPlanLevels = [];
    const apiResponse = await GetPlanTemplateByIdApi(perfCycleState?.template);
    if (apiResponse.success) {
      apiResponse.data?.planLevels?.forEach((level) => {
        if (level.order !== 1) {
          newPlanLevels.push(level);
        }
      });
      setPlanLevels(newPlanLevels);
    }
  }

  async function getActiveScales() {
    const apiResponse = await GetActiveScalesApi();
    if (apiResponse.success) {
      setScales(apiResponse.data);
    }
  }

  async function selectScale(value) {
    setScaleUnits([]);
    let addUnit = "";
    scales.forEach((scale) => {
      if (scale.id === value) {
        if (scale.name === "Number") {
          setAddScaleUnit(false);
          setPercentage(false);
        } else if (scale.name === "Percentage") {
          setAddScaleUnit(false);
          setPercentage(true);
        } else {
          setAddScaleUnit(true);
          setPercentage(false);
          addUnit = "add";
        }
      }
    });
    if (addUnit === "add") {
      const apiResponse = await GetScaleUnitsByScaleApi(value);
      if (apiResponse.message === "success") {
        setScaleUnits(apiResponse.scaleUnits);
      }
      apiResponse.scaleUnits?.forEach((unit) => {
        if (unit.default) {
          form.setFields([
            {
              name: "unitScale",
              value: unit.id,
            },
          ]);
        }
      });
    }
  }
  async function selectPlanLevel(value) {
    planLevels?.forEach((level) => {
      if (level.id === value) {
        setPlanLevel(level);
      }
    });
  }

  const unitSelector = (
    <Form.Item name="unitScale" noStyle>
      <Select style={{ width: 100 }}>
        {scaleUnits.map((unit) => {
          return (
            <Option key={unit.id} value={unit.id}>
              {unit.abbreviation}
            </Option>
          );
        })}
      </Select>
    </Form.Item>
  );

  function renderDropdownAndBooleanAttributes() {
    let output = [];

    // Only render dropdown and boolean attributes
    const dropdownAndBooleanAttributes = props.plan?.planAttributeValue?.filter((attribute) => {
      return attribute.valueType === "dropdown" || attribute.valueType === "boolean";
    }) || [];

    dropdownAndBooleanAttributes.forEach((attribute) => {
      // Find the corresponding template attribute to get dropdown options
      const templateAttribute = planLevel?.planLevelAttributes?.find(
        (templateAttr) => templateAttr.id === attribute.planAttributeId
      );

      output.push(
        <Col span={12} key={attribute.id}>
          <Form.Item
            label={attribute.name}
            name={attribute.id}
          >
            {attribute.valueType === "dropdown" ? (
              <Select
                mode="multiple"
                showSearch
                allowClear
                placeholder={`Select ${attribute.name}`}
                style={{ width: "100%" }}
                getPopupContainer={(trigger) => trigger.parentNode}
                filterOption={(input, option) =>
                  option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                }
              >
                {(templateAttribute?.dropdownOptions || attribute.dropdownOptions)?.map((option) => (
                  <Option key={option.id} value={option.id}>
                    {option.value}
                  </Option>
                ))}
              </Select>
            ) : attribute.valueType === "boolean" ? (
              <Select
                placeholder={`Select ${attribute.name}`}
                style={{ width: "100%" }}
                getPopupContainer={(trigger) => trigger.parentNode}
              >
                <Option value={true}>Yes</Option>
                <Option value={false}>No</Option>
              </Select>
            ) : null}
          </Form.Item>
        </Col>
      );
    });

    return output;
  }

  function renderOtherAttributes() {
    let output = [];

    // Filter out dropdown, boolean, and date attributes (all date attributes handled separately)
    const otherAttributes = props.plan?.planAttributeValue?.filter((attribute) => {
      return !(attribute.valueType === "dropdown" || 
               attribute.valueType === "boolean" || 
               attribute.valueType === "date");
    }) || [];

    otherAttributes.forEach((attribute) => {
      // Only render supported attribute types to avoid empty form items
      if (attribute.valueType === "text" || attribute.valueType === "number" || attribute.valueType === "double") {
        output.push(
          <Col span={12} key={attribute.id}>
            <Form.Item
              label={attribute.name}
              name={attribute.id}
              initialValue={
                attribute.value === null ? "" : attribute.value
              }
            >
              {attribute.valueType === "text" ? (
                <Input style={{ width: "100%" }} placeholder={attribute.name} />
              ) : attribute.valueType === "number" ? (
                <InputNumber
                  min={0}
                  style={{ width: '100%' }} 
                  placeholder={attribute.name}
                />
              ) : attribute.valueType === "double" ? (
                <InputNumber
                  min={0}
                  step={0.01}
                  style={{ width: '100%' }} 
                  placeholder={attribute.name}
                />
              ) : null}
            </Form.Item>
          </Col>
        );
      }
    });

    return output;
  }



  return (
    <>
      <Modal
        title={`Edit split Plan`}
        visible={props.visible}
        onCancel={() => handleCancel()}
        footer={null}
        destroyOnClose={true}
        width={900}
      >
        <Form
          name="splitPlan"
          onFinish={onFinish}
          autoComplete="off"
          layout="vertical"
          requiredMark={false}
          form={form}
          initialValues={{
            planGroup: props.plan?.planGroup,
            planLevel: props.plan?.planLevel,
            title: props.plan?.title,
          }}
          onValuesChange={() => {
            setValuesChanged(true);
          }}
        >
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item label="Plan Group*" name="planGroup">
                <Input disabled placeholder="Plan group" />
              </Form.Item>
            </Col>

            <Col span={12}>
              <Form.Item label="Plan Level" name="planLevel">
                <Input disabled placeholder="Plan level" />
              </Form.Item>
            </Col>

            <Col span={12}>
              <Form.Item
                label={`Title*`}
                name="title"
                rules={[
                  {
                    required: true,
                    message: `Please enter title!`,
                  },
                ]}
              >
                <Input placeholder={"title"} />
              </Form.Item>
            </Col>

            <Col span={12}>
              <Form.Item
                label="Assign to"
                name="assignTo"
                rules={[
                  {
                    required: true,
                    message: "Please select a user!",
                  },
                ]}
              >
                <Select
                  showSearch
                  placeholder="Select user"
                  optionFilterProp="children"
                  filterOption={(input, option) =>
                    option.children
                      .toLowerCase()
                      .indexOf(input.toLowerCase()) >= 0
                  }
                  filterSort={(optionA, optionB) =>
                    optionA.children
                      .toLowerCase()
                      .localeCompare(optionB.children.toLowerCase())
                  }
                >
                  {unitPositions.map((position) => {
                    return (
                      <Option key={position.id} value={position.id}>
                        {(position.user?.firstName
                          ? position.user?.firstName + ", "
                          : "") + position.designation?.name}
                      </Option>
                    );
                  })}
                </Select>
              </Form.Item>
            </Col>

            {planLevel.target && (
              <>
                <Col span={12}>
                  <Form.Item
                    label="Target scale*"
                    name="targetScale"
                    rules={[
                      {
                        required: true,
                        message: "Please select a target scale!",
                      },
                    ]}
                  >
                    <Select
                      showSearch
                      placeholder="Select a scale"
                      optionFilterProp="children"
                      filterOption={(input, option) =>
                        option.children
                          .toLowerCase()
                          .indexOf(input.toLowerCase()) >= 0
                      }
                      filterSort={(optionA, optionB) =>
                        optionA.children
                          .toLowerCase()
                          .localeCompare(optionB.children.toLowerCase())
                      }
                      onChange={(value) => selectScale(value)}
                    >
                      {scales.map((scale) => {
                        return (
                          <Option key={scale.id} value={scale.id}>
                            {scale.name}
                          </Option>
                        );
                      })}
                    </Select>
                  </Form.Item>
                </Col>

                <Col span={12}>
                  <Form.Item
                    label="Target*"
                    name="target"
                    rules={[
                      {
                        required: true,
                        message: "Please enter target!",
                      },
                    ]}
                  >
                    <InputNumber
                      min={0}
                      addonAfter={
                        addScaleUnit ? unitSelector : percentage && "%"
                      }
                      style={{ width: "100%" }}
                    />
                  </Form.Item>
                </Col>
              </>
            )}

            {planLevel.description && (
              <Col span={24}>
                <Form.Item label="Description" name="description">
                  <TextArea
                    showCount
                    maxLength={600}
                    placeholder="Description"
                  />
                </Form.Item>
              </Col>
            )}

            {planLevel && renderDropdownAndBooleanAttributes()}

            {planLevel && planLevel.targetDate && (
              <Col span={12}>
                <Form.Item label="Target date*" name="targetDate">
                  <DatePicker className="w-full" placeholder="Target date" />
                </Form.Item>
              </Col>
            )}

            {planLevel && renderOtherAttributes()}

            <Col span={24}>
              <Form.Item style={{ marginBottom: 0 }}>
                <div className=" flex justify-end">
                  <Button className="mr-3 w-28" onClick={() => handleCancel()}>
                    Cancel
                  </Button>
                  <Button
                    className="btn w-32"
                    type="primary"
                    htmlType={submit && "submit"}
                    disabled={submit || !valuesChanged}
                  >
                    {submit ? (
                      <Spin
                        size="small"
                        indicator={
                          <LoadingOutlined style={{ color: "#fff" }} spin />
                        }
                      />
                    ) : (
                      `Save`
                    )}
                  </Button>
                </div>
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Modal>
    </>
  );
}

export default EditSplitPlanModal;
